import { motion } from "motion/react";
import Button from "../Button";
import { FaPen } from "react-icons/fa6";
import EventModal from "../../pages/projects/pages/project/components/EventModal";
import { useState } from "react";
import { useUser } from "../../contexts/UserContext/hooks/useUser";
import EventPlanning from "../../pages/projects/pages/project/components/EventPlanning";
import EventTickets from "../EventTickets";
import EventDraftButton from "./components/EventDraftButton";
import EventDuplicateButton from "./components/EventDuplicateButton";
import useNav from "../../hooks/useNavigation";
import ParticipantsInlineList from "./components/ParticipantsInlineList";
import EventDocuments from "../EventDocuments";
import EventContacts from "./components/EventContacts";
import Location from "../Location";
import { EventDetailsProvider } from "./context/provider";
import { EventFragment } from "../../__generated__/graphql";
import ResourceViewsComponent from "../ResourceViewsComponent";

interface EventDetailsProps {
  event?: EventFragment | null;
  refetchProjectDetails: () => void;
}

export default function EventDetails({
  event,
  refetchProjectDetails,
}: EventDetailsProps) {
  const { currentOrganization } = useUser();
  const navigateWithLog = useNav();

  const [isEditingEvent, setIsEditingEvent] = useState(false);

  const isOwner = currentOrganization?.id === event?.ownerOrganizationId;

  if (!event) return null;

  const eventHeader = () => {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <div className="flex flex-row items-center gap-2">
            {isOwner ? <EventDraftButton event={event} /> : null}
            <div className="text-xl font-bold text-primary truncate max-w-[300px]">
              {event.name}
            </div>
          </div>

          {isOwner ? (
            <div className="flex flex-row items-center gap-2">
              <EventDuplicateButton
                event={event}
                onNewEvent={(event) => {
                  navigateWithLog(`/events/${event.id}`);
                }}
              />

              <Button
                variant="secondary"
                className="flex flex-row gap-2 text-sm text-secondary border-0 hover:text-secondary/80"
                onClick={() => setIsEditingEvent(true)}
              >
                <FaPen className="w-4 h-4" />
                Modifier
              </Button>
            </div>
          ) : null}
        </div>

        <ResourceViewsComponent
          resourceViews={event.resourceViews ?? []}
          participants={
            event.eventParticipants
              ?.filter((participant) => !!participant)
              .map((participant) => participant?.publicUserOrganization) ?? []
          }
        />
      </div>
    );
  };

  return (
    <>
      <EventModal
        isOpen={isEditingEvent}
        onClose={() => {
          setIsEditingEvent(false);
        }}
        defaultEvent={event}
      />

      <EventDetailsProvider>
        <motion.div
          className="flex flex-col gap-4 relative"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          transition={{ duration: 0.3 }}
        >
          {eventHeader()}

          <Location location={event.eventLocation} />

          <div className="flex flex-col gap-4">
            <ParticipantsInlineList event={event} />
            <div className="flex gap-4">
              <div className="flex-2 flex flex-col gap-4">
                <EventPlanning event={event} />
                <EventDocuments
                  event={event}
                  refetchProjectDetails={refetchProjectDetails}
                />
              </div>
              <div className="flex-1 flex flex-col gap-4">
                <div className="h-50">
                  <EventTickets event={event} />
                </div>
                <EventContacts event={event} />
              </div>
            </div>
          </div>
        </motion.div>
      </EventDetailsProvider>
    </>
  );
}
